import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { API_BASE, apiFetch } from "../../utils/api";
import "./AdminGallery.css";

/* webpack require.context for gallery images */
let GALLERY_IMAGES = {};
try {
  const ctx = require.context(
    "../../assets/uploads/gallery",
    false,
    /\.(png|jpe?g|gif|webp|svg)$/i
  );
  GALLERY_IMAGES = ctx.keys().reduce((map, key) => {
    map[key.replace(/^\.\//, "")] = ctx(key);
    return map;
  }, {});
} catch (_) { /* falls back to API URL */ }

function galleryImg(filename) {
  if (!filename) return null;
  return GALLERY_IMAGES[filename] ?? `${API_BASE}/uploads/gallery/${filename}`;
}

/* Prettify a slug for display: "about-us" → "About Us" */
function prettifySlug(slug) {
  if (!slug) return "";
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const EMPTY_FORM = {
  title:       "",
  description: "",
  order:       "0",
  active:      1,
  page_slugs:  [], // array of strings
  image:       null,
};

/* ─────────────────────────────────
   Dot-menu
   ───────────────────────────────── */
function DotMenu({ onView, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="agl-dot-menu" ref={ref}>
      <button
        className="agl-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="agl-dot-menu__dropdown">
          <button onClick={() => { onView();   setOpen(false); }}>
            <span className="agl-dot-menu__icon">👁</span> View
          </button>
          <button onClick={() => { onEdit();   setOpen(false); }}>
            <span className="agl-dot-menu__icon">✏</span> Edit
          </button>
          <button
            className="agl-dot-menu__delete"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="agl-dot-menu__icon">🗑</span> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────
   Status badge
   ───────────────────────────────── */
function StatusBadge({ active }) {
  const isActive = Number(active) === 1;
  return (
    <span className={`agl-badge ${isActive ? "agl-badge--active" : "agl-badge--inactive"}`}>
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

/* ─────────────────────────────────
   Page slug pill tags
   ───────────────────────────────── */
function SlugPills({ slugs }) {
  if (!slugs || slugs.length === 0)
    return <span className="agl-no-pages">—</span>;
  return (
    <div className="agl-slug-pills">
      {slugs.map((s) => (
        <span key={s} className="agl-slug-pill">{prettifySlug(s)}</span>
      ))}
    </div>
  );
}

/* ─────────────────────────────────
   Page slug multi-select input
   Allows typing a new slug and adding
   it as a tag, plus removing existing.
   ───────────────────────────────── */
function SlugInput({ value, onChange, knownSlugs }) {
  const [inputVal, setInputVal] = useState("");
  const [showSugg, setShowSugg] = useState(false);
  const wrapRef = useRef(null);

  /* Close suggestions on outside click */
  useEffect(() => {
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setShowSugg(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const suggestions = knownSlugs.filter(
    (s) => s.toLowerCase().includes(inputVal.toLowerCase()) && !value.includes(s)
  );

  const addSlug = (slug) => {
    const trimmed = slug.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    if (!trimmed || value.includes(trimmed)) { setInputVal(""); return; }
    onChange([...value, trimmed]);
    setInputVal("");
    setShowSugg(false);
  };

  const removeSlug = (slug) => onChange(value.filter((s) => s !== slug));

  const handleKey = (e) => {
    if ((e.key === "Enter" || e.key === ",") && inputVal.trim()) {
      e.preventDefault();
      addSlug(inputVal);
    }
    if (e.key === "Backspace" && !inputVal && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="agl-slug-input-wrap" ref={wrapRef}>
      <div className="agl-slug-input-box" onClick={() => document.getElementById("agl-slug-text-input")?.focus()}>
        {value.map((s) => (
          <span key={s} className="agl-slug-input-tag">
            {prettifySlug(s)}
            <button type="button" onClick={(e) => { e.stopPropagation(); removeSlug(s); }}>×</button>
          </span>
        ))}
        <input
          id="agl-slug-text-input"
          type="text"
          value={inputVal}
          placeholder={value.length === 0 ? "Type a page slug and press Enter…" : ""}
          onChange={(e) => { setInputVal(e.target.value); setShowSugg(true); }}
          onFocus={() => setShowSugg(true)}
          onKeyDown={handleKey}
          autoComplete="off"
        />
      </div>
      {showSugg && suggestions.length > 0 && (
        <div className="agl-slug-suggestions">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              className="agl-slug-suggestion"
              onMouseDown={(e) => { e.preventDefault(); addSlug(s); }}
            >
              {prettifySlug(s)}<span className="agl-slug-suggestion__raw">/{s}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════
   VIEW MODAL
   ═══════════════════════════════════ */
function ViewModal({ image, onClose }) {
  if (!image) return null;

  return (
    <div className="agl-modal-overlay" onClick={onClose}>
      <div className="agl-modal agl-modal--view" onClick={(e) => e.stopPropagation()}>
        <div className="agl-modal__header">
          <div className="agl-modal__title-row">
            <span className="agl-modal__tag">VIEW</span>
            <h2 className="agl-modal__title">{image.title || "Gallery Image"}</h2>
          </div>
          <button className="agl-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="agl-modal__body agl-modal__body--view">
          {/* Full-size image */}
          {image.image_filename ? (
            <img
              src={galleryImg(image.image_filename)}
              alt={image.title || ""}
              className="agl-view-image"
            />
          ) : (
            <div className="agl-view-image--placeholder"><span>🖼</span></div>
          )}

          {/* Details grid */}
          <div className="agl-view-grid">
            {[
              ["Title",    image.title  || "—"],
              ["Order",    image.order  ?? "—"],
              ["Status",   Number(image.active) === 1 ? "Active" : "Inactive"],
              ["File",     image.image_filename || "—"],
              ["Created",  image.created_at ? new Date(image.created_at).toLocaleDateString("en-ZA") : "—"],
              ["Updated",  image.updated_at ? new Date(image.updated_at).toLocaleDateString("en-ZA") : "—"],
            ].map(([label, val]) => (
              <div key={label} className="agl-view-row">
                <span className="agl-view-row__label">{label}</span>
                <span className="agl-view-row__val">{val}</span>
              </div>
            ))}
          </div>

          {/* Pages */}
          <div className="agl-view-section">
            <h4 className="agl-view-section__title">Pages</h4>
            <SlugPills slugs={image.page_slugs || []} />
          </div>

          {/* Description */}
          {image.description && (
            <div className="agl-view-section">
              <h4 className="agl-view-section__title">Description</h4>
              <p className="agl-view-description__text">{image.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   CREATE / EDIT MODAL
   ═══════════════════════════════════ */
function FormModal({ mode, image, onClose, onSaved, knownSlugs }) {
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [preview,  setPreview]  = useState(null);
  const [clearImg, setClearImg] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");

  /* Populate when editing */
  useEffect(() => {
    if (mode === "edit" && image) {
      setForm({
        title:       image.title       || "",
        description: image.description || "",
        order:       image.order       ?? "0",
        active:      Number(image.active) === 1 ? 1 : 0,
        page_slugs:  image.page_slugs  || [],
        image:       null,
      });
      setPreview(image.image_filename ? galleryImg(image.image_filename) : null);
      setClearImg(false);
    }
  }, [mode, image]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleFile = (file) => {
    if (!file) return;
    set("image", file);
    setClearImg(false);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleClear = () => {
    set("image", null);
    setPreview(null);
    setClearImg(true);
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.image && mode === "create" && !preview)
      return setError("Please upload an image.");

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title",       form.title.trim());
      fd.append("description", form.description || "");
      fd.append("order",       parseInt(form.order) || 0);
      fd.append("active",      form.active);
      /* Send page_slugs as JSON string — server will parse */
      fd.append("page_slugs",  JSON.stringify(form.page_slugs));
      if (form.image) fd.append("image_filename", form.image);
      if (clearImg)   fd.append("clear_image", "1");

      if (mode === "create") {
        await apiFetch("/api/admin/gallery", { method: "POST", body: fd });
      } else {
        await apiFetch(`/api/admin/gallery/${image.id}`, { method: "PUT", body: fd });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="agl-modal-overlay" onClick={onClose}>
      <div className="agl-modal agl-modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="agl-modal__header">
          <div className="agl-modal__title-row">
            <span className="agl-modal__tag">{mode === "create" ? "NEW" : "EDIT"}</span>
            <h2 className="agl-modal__title">
              {mode === "create" ? "Add Image" : "Edit Image"}
            </h2>
          </div>
          <button className="agl-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="agl-modal__body agl-modal__body--form">
          {error && <div className="agl-modal__error">⚠ {error}</div>}

          {/* ── Section: Image Upload ── */}
          <div className="agl-form-section">
            <h3 className="agl-form-section__title">
              Image {mode === "create" && <span className="req">*</span>}
            </h3>
            <label className="agl-image-upload">
              {preview ? (
                <img src={preview} alt="" className="agl-image-upload__preview" />
              ) : (
                <div className="agl-image-upload__placeholder">
                  <span>🖼</span>
                  <span>Click to upload image</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => handleFile(e.target.files[0])}
              />
              <span className="agl-image-upload__label">
                {preview ? "Click to change image" : "Upload Image"}
              </span>
            </label>
            {preview && (
              <button
                type="button"
                className="agl-image-clear"
                style={{ marginTop: 6 }}
                onClick={handleClear}
              >
                Remove image
              </button>
            )}
            <p className="agl-form-hint">
              Saved to <code>src/assets/uploads/gallery/</code>. Leave blank to keep existing.
            </p>
          </div>

          {/* ── Section: Image Details ── */}
          <div className="agl-form-section">
            <h3 className="agl-form-section__title">Image Details</h3>
            <div className="agl-form-grid">
              <div className="agl-form-field agl-form-field--full">
                <label>Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="e.g. Workshop Interior"
                />
              </div>

              <div className="agl-form-field agl-form-field--full">
                <label>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Optional caption or description for this image…"
                />
              </div>
            </div>
          </div>

          {/* ── Section: Page Assignment ── */}
          <div className="agl-form-section">
            <h3 className="agl-form-section__title">Page Assignment</h3>
            <div className="agl-form-field">
              <label>Pages <span className="agl-form-label--sub">(which pages show this image)</span></label>
              <SlugInput
                value={form.page_slugs}
                onChange={(val) => set("page_slugs", val)}
                knownSlugs={knownSlugs}
              />
              <p className="agl-form-hint">
                Type a page slug (e.g. <code>home</code>, <code>about-us</code>, <code>services</code>) and press <kbd>Enter</kbd> to add. An image can appear on multiple pages.
              </p>
            </div>
          </div>

          {/* ── Section: Display Options ── */}
          <div className="agl-form-section">
            <h3 className="agl-form-section__title">Display Options</h3>
            <div className="agl-form-grid">
              <div className="agl-form-field">
                <label>Display Order</label>
                <input
                  type="number"
                  min="0"
                  value={form.order}
                  onChange={(e) => set("order", e.target.value)}
                  placeholder="0"
                />
                <p className="agl-form-hint">Lower numbers appear first.</p>
              </div>

              <div className="agl-form-field agl-form-field--check-col">
                <label className="agl-form-label--block">Visibility</label>
                <label className="agl-check">
                  <input
                    type="checkbox"
                    checked={Number(form.active) === 1}
                    onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                  />
                  <span>Active (visible on site)</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="agl-modal__footer">
          <button className="agl-btn agl-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="agl-btn agl-btn--save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : mode === "create" ? "Add Image" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE MODAL
   ═══════════════════════════════════ */
function DeleteModal({ image, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await apiFetch(`/api/admin/gallery/${image.id}`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="agl-modal-overlay" onClick={onClose}>
      <div className="agl-modal agl-modal--delete" onClick={(e) => e.stopPropagation()}>
        <div className="agl-delete__icon">🗑</div>
        <h2 className="agl-delete__title">Delete Image?</h2>
        <p className="agl-delete__body">
          You are about to permanently delete{" "}
          <strong>{image?.title || "this gallery image"}</strong>. This will remove
          the image file and all page assignments. This action cannot be undone.
        </p>
        {error && <div className="agl-modal__error">⚠ {error}</div>}
        <div className="agl-delete__actions">
          <button className="agl-btn agl-btn--cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button className="agl-btn agl-btn--delete" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminGallery() {
  const [images,     setImages]     = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  /* Known page slugs for autocomplete */
  const [knownSlugs, setKnownSlugs] = useState([]);

  /* Filters */
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pageFilter,   setPageFilter]   = useState("");
  const [sort,         setSort]         = useState("order_asc");
  const [page,         setPage]         = useState(1);
  const searchTimer = useRef(null);

  /* Modals */
  const [modal,  setModal]  = useState(null);
  const [target, setTarget] = useState(null);

  /* ── Load known slugs once for autocomplete ── */
  useEffect(() => {
    apiFetch("/api/admin/gallery/slugs")
      .then((data) => setKnownSlugs(data.slugs || []))
      .catch(() => {});
  }, []);

  /* ── Load gallery images ── */
  const loadImages = useCallback(() => {
    setLoading(true);
    setError("");
    const q = new URLSearchParams();
    if (search)       q.set("search", search);
    if (statusFilter) q.set("active", statusFilter);
    if (pageFilter)   q.set("page_slug", pageFilter);
    q.set("sort",  sort);
    q.set("page",  page);
    q.set("limit", "20");

    apiFetch(`/api/admin/gallery?${q}`)
      .then((data) => {
        setImages(data.images      || []);
        setPagination(data.pagination || { page: 1, total: 0, pages: 1 });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, statusFilter, pageFilter, sort, page]);

  useEffect(() => { loadImages(); }, [loadImages]);

  /* ── Handlers ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setPage(1), 400);
  };

  const openModal  = (type, item = null) => { setModal(type); setTarget(item); };
  const closeModal = () => { setModal(null); setTarget(null); };
  const afterSave  = () => {
    closeModal();
    loadImages();
    /* refresh known slugs in case a new one was added */
    apiFetch("/api/admin/gallery/slugs")
      .then((data) => setKnownSlugs(data.slugs || []))
      .catch(() => {});
  };

  return (
    <AdminLayout pageTag="CONTENT" pageTitle="Gallery">

      {error && <div className="agl-error-bar">⚠ {error}</div>}

      {/* ── Toolbar ── */}
      <div className="agl-toolbar">
        <div className="agl-toolbar__left">
          <div className="agl-search-wrap">
            <span className="agl-search-wrap__icon">🔍</span>
            <input
              className="agl-search"
              type="text"
              placeholder="Search by title or description…"
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <button
                className="agl-search-wrap__clear"
                onClick={() => { setSearch(""); setPage(1); }}
              >✕</button>
            )}
          </div>

          <select
            className="agl-filter-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>

          {/* Page filter — populated from known slugs */}
          <select
            className="agl-filter-select"
            value={pageFilter}
            onChange={(e) => { setPageFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Pages</option>
            {knownSlugs.map((s) => (
              <option key={s} value={s}>{prettifySlug(s)}</option>
            ))}
          </select>

          <select
            className="agl-filter-select"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
          >
            <option value="order_asc">Order ↑</option>
            <option value="order_desc">Order ↓</option>
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="title_asc">Title A–Z</option>
          </select>
        </div>

        <div className="agl-toolbar__right">
          <span className="agl-toolbar__count">
            {loading
              ? "…"
              : `${pagination.total} image${pagination.total !== 1 ? "s" : ""}`}
          </span>
          <button className="agl-btn-primary" onClick={() => openModal("create")}>
            + Add Image
          </button>
        </div>
      </div>

      {/* ── Grid / Table ── */}
      {loading ? (
        <div className="agl-table-skeleton" />
      ) : images.length === 0 ? (
        <div className="agl-empty">
          <span>🖼</span>
          <p>No gallery images found.</p>
          {(search || statusFilter || pageFilter) && (
            <button
              className="agl-btn agl-btn--cancel"
              onClick={() => { setSearch(""); setStatusFilter(""); setPageFilter(""); setPage(1); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="agl-table-wrap">
          <table className="agl-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Description</th>
                <th>Pages</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {images.map((img) => (
                <tr key={img.id}>
                  <td>
                    {img.image_filename ? (
                      <img
                        src={galleryImg(img.image_filename)}
                        alt={img.title || ""}
                        className="agl-img-thumb"
                      />
                    ) : (
                      <span className="agl-no-img">—</span>
                    )}
                  </td>
                  <td>
                    <span className="agl-title-cell">{img.title || <em className="agl-untitled">Untitled</em>}</span>
                  </td>
                  <td>
                    <span className="agl-desc-preview">
                      {img.description
                        ? img.description.slice(0, 100) + (img.description.length > 100 ? "…" : "")
                        : "—"}
                    </span>
                  </td>
                  <td>
                    <SlugPills slugs={img.page_slugs || []} />
                  </td>
                  <td>
                    <span className="agl-order-badge">{img.order ?? 0}</span>
                  </td>
                  <td>
                    <StatusBadge active={img.active} />
                  </td>
                  <td>
                    <DotMenu
                      onView={()   => openModal("view",   img)}
                      onEdit={()   => openModal("edit",   img)}
                      onDelete={() => openModal("delete", img)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {pagination.pages > 1 && (
        <div className="agl-pagination">
          <button
            className="agl-pagination__btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >← Prev</button>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`agl-pagination__btn ${p === page ? "active" : ""}`}
              onClick={() => setPage(p)}
            >{p}</button>
          ))}
          <button
            className="agl-pagination__btn"
            disabled={page >= pagination.pages}
            onClick={() => setPage((p) => p + 1)}
          >Next →</button>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "view"   && <ViewModal image={target}   onClose={closeModal} />}
      {modal === "create" && (
        <FormModal mode="create" onClose={closeModal} onSaved={afterSave} knownSlugs={knownSlugs} />
      )}
      {modal === "edit" && (
        <FormModal mode="edit" image={target} onClose={closeModal} onSaved={afterSave} knownSlugs={knownSlugs} />
      )}
      {modal === "delete" && <DeleteModal image={target} onClose={closeModal} onDeleted={afterSave} />}

    </AdminLayout>
  );
}